import React, { useState, useEffect, useRef } from "react";
import { Button, Row, Col,  Card } from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import MemberSearch from "../../../components/MemberSearch";
import MembershipMoto from "../../../components/MembershipMoto";
import { apiGet, renewMemberPaymentApi } from "../../../Services";
import { toast } from "react-toastify";
import { useSearchParams } from "react-router-dom";
import { Spinner } from "react-bootstrap";

function SearchMember() {
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const [member, setMember] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);
  const memberDetailsRef = useRef(null);

  // Renewal payment states (PhonePe)
  const [isPhonePeScriptLoaded, setIsPhonePeScriptLoaded] = useState(false);
  const [renewLoading, setRenewLoading] = useState(false);
  const [renewOrderId, setRenewOrderId] = useState(null);
  const [renewTransactionDetails, setRenewTransactionDetails] = useState(null);
  const getErrorMessage = (err, fallback) =>
    err?.response?.data?.error ||
    err?.response?.data?.message ||
    err?.message ||
    fallback;

  useEffect(() => {
    if (member && window.innerWidth < 768) {
      memberDetailsRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [member]);

  // On this page, block right-click and common copy shortcuts.
  useEffect(() => {
    const handleContextMenu = (e) => {
      e.preventDefault();
    };
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && ["c", "x", "a"].includes(e.key.toLowerCase())) {
        e.preventDefault();
      }
    };

    document.addEventListener("contextmenu", handleContextMenu);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("contextmenu", handleContextMenu);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  // Load PhonePe checkout script once for renewal payments
  useEffect(() => {
    const src = "https://mercury.phonepe.com/web/bundle/checkout.js";
    const existingScript = document.querySelector(`script[src="${src}"]`);
    if (existingScript) {
      setIsPhonePeScriptLoaded(true);
      return;
    }

    const script = document.createElement("script");
    script.src = src;
    script.onload = () => setIsPhonePeScriptLoaded(true);
    script.onerror = () => {
      toast.error("Payment gateway failed to load. Please refresh.");
    };
    document.body.appendChild(script);

    return () => {
      // Keep script for subsequent payments; no cleanup needed.
    };
  }, []);

  useEffect(() => {
    setLoading(true);
    setMember(null);
    if (id) {
      const normalizedId = id
        .toUpperCase()
        .replace(/\s+/g, "")
        .replace(/\//g, "_");
      apiGet(`/api/members/public/by-reg/${normalizedId}`)
        .then((res) => {
          setMember(res.data);
          setNotFound(false);
        })
        .catch((err) => {
          if (err.response?.status === 404) {
            setNotFound(true);
          } else {
            toast.error(getErrorMessage(err, "Something went wrong. Please try again."));
          }
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [id]);

  const isExpired =
    member?.expiry_date &&
    new Date(member.expiry_date) <= new Date(new Date().toDateString());

  const isExpiringSoon =
    member?.expiry_date &&
    !isExpired &&
    (() => {
      const expiryDate = new Date(member.expiry_date);
      if (Number.isNaN(expiryDate.getTime())) return false;

      // Compare using "date only" to avoid timezone/time-of-day issues.
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      expiryDate.setHours(0, 0, 0, 0);

      const MS_PER_DAY = 24 * 60 * 60 * 1000;
      const diffDays = Math.ceil((expiryDate.getTime() - today.getTime()) / MS_PER_DAY);
      return diffDays >= 0 && diffDays <= 30;
    })();

  const hasSuccessfulRenewalPayment = Boolean(
    member?.renewal_payment?.has_successful_renewal_payment
  );

  // Renewal APIs expect the internal `memberId` (not the registration id).
  // Public search responses differ slightly by backend; support common id field names.
  const resolveMemberId = (m) =>
    m?.id ??
    m?._id ??
    m?.member_id ??
    m?.memberId ??
    m?.memberID ??
    m?.Member?.id ??
    m?.Member?._id ??
    m?.Member?.member_id;

  const handleRenewPaymentStatus = (orderId) => {
    setRenewLoading(true);
    apiGet(`/api/payment/status/${orderId}`)
      .then((res) => {
        if (res.data?.success) {
          setRenewTransactionDetails(res.data.response);
          toast.success("Renewal payment successful!");
        } else {
          toast.error(
            res.data?.error || res.data?.message || "Renewal payment failed. Please try again."
          );
        }
      })
      .catch((err) => toast.error(getErrorMessage(err, "Something went wrong. Please try again.")))
      .finally(() => setRenewLoading(false));
  };

  const handleRenewPayment = async () => {
    const memberId = resolveMemberId(member);
    if (!memberId) {
      toast.error("Member id not found. Please try again later.");
      return;
    }

    if (!isPhonePeScriptLoaded || !window.PhonePeCheckout) {
      toast.error(
        "Payment gateway is still loading. Please try again in a moment."
      );
      return;
    }

    setRenewTransactionDetails(null);
    setRenewOrderId(null);
    setRenewLoading(true);

    try {
      const res = await renewMemberPaymentApi(memberId);
      const payload = res.data;

      const orderId = payload?.transactionId ?? payload?.orderId ?? payload?.merchantOrderId;
      const tokenUrl =
        payload?.response?.redirectUrl ??
        payload?.response?.tokenUrl ??
        payload?.response?.redirect_url;

      if (!orderId || !tokenUrl) {
        toast.error(payload?.message || "Failed to start renewal payment.");
        return;
      }

      setRenewOrderId(orderId);

      const callback = (response) => {
        if (response === "USER_CANCEL") {
          toast.error("Payment cancelled!");
          return;
        }
        if (response === "CONCLUDED") {
          handleRenewPaymentStatus(orderId);
          return;
        }
      };

      window.PhonePeCheckout.transact({
        tokenUrl,
        callback,
        type: "IFRAME",
      });
    } catch (err) {
      const msg = getErrorMessage(err, "Failed to initiate payment");
      toast.error(msg);
      setRenewLoading(false);
    }
  };

  return (
    <div
      className="container my-4"
      style={{
        fontFamily: "Poppins, sans-serif",
        fontSize: "14px",
        padding: "0 12px",
      }}
      onCopy={(e) => e.preventDefault()}
      onCut={(e) => e.preventDefault()}
      onContextMenu={(e) => e.preventDefault()}
    >
      <Helmet>
        <title>Search Membership | Manav Adhikar Forum</title>
        <meta
          name="description"
          content="Search for membership in our Human Rights Organization. Fill in your details, upload required documents, and join our mission."
        />
        <meta
          name="keywords"
          content="Manav Adhikar Forum, human rights, equality, justice, India, MAF, awareness, campaigns"
        />
        <link rel="canonical" href="https://manavadhikarforum.in/Membership" />
      </Helmet>

      <Row className="align-items-stretch">
        {/* Left Side Placeholder */}
        <Col md={5} className="d-flex flex-column ">
          <div className="mb-3">
            <MemberSearch />
          </div>

          <div className="flex-grow-1 d-flex">
            <MembershipMoto className="w-100 h-100" />
          </div>
        </Col>

        {/* Right Side Form */}
        <Col
          md={7}
          className="d-flex top-margin-sm-container position-relative"
          ref={memberDetailsRef}
        >
          <div className="p-4 border-0 rounded-3 shadow-lg bg-white flex-grow-1">
            <div className="d-flex flex-column mb-4 text-center align-items-center">
              <h4 className="fw-bold " style={{ color: "#1a5276" }}>
                Member Details
                <div
                  className="rounded-pill mt-2 bg-warning"
                  style={{
                    height: "5px",
                    width: "15%",
                  }}
                ></div>
              </h4>
            </div>
            {/* View detailss */}
            {notFound && (
              <div className="text-center p-4">
                <h5 className="fw-bold mb-3">No Member Found</h5>
                <p className="text-muted">
                  Please check the registration ID spelling, or if the issue
                  continues{" "}
                  <a
                    href="/Contact-Us"
                    className="text-primary text-decoration-underline"
                  >
                    contact us
                  </a>
                  .
                </p>
              </div>
            )}

            {loading ? (
              <div className="d-flex justify-content-center align-items-center position-absolute top-0 start-0 w-100 h-100 bg-white bg-opacity-75">
                <Spinner animation="border" variant="primary" />
              </div>
            ) : (
              member && (
                <Card
                  className="shadow-lg border-0 w-100"
                  style={{
                    borderRadius: "15px",
                    overflow: "hidden",
                    background: "linear-gradient(180deg, #fdfdfd, #f5f8fb)",
                  }}
                  onCopy={(e) => e.preventDefault()}
                  onCut={(e) => e.preventDefault()}
                  onPaste={(e) => e.preventDefault()}
                  onContextMenu={(e) => e.preventDefault()}
                  onSelectStart={(e) => e.preventDefault()}
                  onDragStart={(e) => e.preventDefault()}
                  onKeyDown={(e) => {
                    if (
                      (e.ctrlKey || e.metaKey) &&
                      ["c", "x", "a"].includes(e.key.toLowerCase())
                    ) {
                      e.preventDefault();
                    }
                  }}
                >
                  {/* Header band */}
                  <div
                    style={{
                      backgroundColor: "#1a5276",
                      color: "white",
                      padding: "20px",
                      textAlign: "center",
                    }}
                  >
                    <h4 className="mb-0 fw-bold">Membership Card</h4>
                  </div>

                  <Card.Body className="px-4 py-5">
                    <div className="text-center mb-4">
                      <div
                        style={{
                          width: "150px",
                          height: "150px",
                          borderRadius: "50%",
                          overflow: "hidden",
                          border: "5px solid #1a5276",
                          margin: "0 auto",
                          boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
                          transition: "transform 0.3s ease",
                        }}
                      >
                        <img
                          src={`${member.photo_url}`}
                          alt={member.name}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      </div>
                      <h4 className="mt-3 fw-bold" style={{ color: "#1a5276" }}>
                        {member.name.toUpperCase()}
                      </h4>
                      <p className="text-muted mb-1">
                        {member.profession.toUpperCase()}
                      </p>

                      {member.DesignationAssignments &&
                        member.DesignationAssignments.length > 0 && (
                          <div className="mt-2" style={{ fontSize: "18px" }}>
                            {member.DesignationAssignments.map(
                              (assign, idx) => {
                                const role =
                                  assign.Designation?.name || "Member";
                                const scope = assign.Designation?.scope || "";
                                let displayText = "";
                                if (scope === "National") {
                                  displayText = `National ${role}`;
                                } else if (scope === "Block") {
                                  displayText = `Block ${role}, ${assign.block}, ${assign.district}, ${assign.state}`;
                                } else if (scope === "District") {
                                  displayText = `District ${role}, ${assign.district}, ${assign.state}`;
                                } else if (scope === "State") {
                                  displayText = `State ${role}, ${assign.state}`;
                                } else {
                                  displayText = `${role}`;
                                }

                                return (
                                  <div
                                    key={idx}
                                    className="fw-semibold text-primary small"
                                  >
                                    {displayText}
                                  </div>
                                );
                              },
                            )}
                          </div>
                        )}
                    </div>

                    {/* Info grid */}
                    <Row className="gy-3">
                      <Col xs={12} md={6}>
                        <div className="p-2 border rounded bg-light">
                          <strong>Registration ID:</strong> <br />{" "}
                          {member.registration_id.toUpperCase()}
                        </div>
                      </Col>
                      {/* <Col xs={12} md={6}>
                        <div className="p-2 border rounded bg-light">
                          <strong>DOB:</strong> <br /> {member.dob}
                        </div>
                      </Col> */}
                      <Col xs={12} md={6}>
                        <div className="p-2 border rounded bg-light">
                          <strong>Mobile:</strong> <br /> {member.mobile_no}
                        </div>
                      </Col>
                      <Col xs={12} md={6}>
                        <div className="p-2 border rounded bg-light">
                          <strong>Email:</strong> <br /> {member.email || "N/A"}
                        </div>
                      </Col>
                      <Col xs={12} md={6}>
                        <div className="p-2 border rounded bg-light">
                          <strong>Aadhaar:</strong> <br /> {member.aadhaar_no}
                        </div>
                      </Col>
                      <Col xs={12} md={6}>
                        <div className="p-2 border rounded bg-light">
                          <strong>Vehicle No:</strong> <br />{" "}
                          {member.vehicle_no
                            ? member.vehicle_no.toUpperCase()
                            : "N/A"}
                        </div>
                      </Col>
                      <Col xs={12} md={6}>
                        <div className="p-2 border rounded bg-light">
                          <strong>Address:</strong> <br /> {member.block},{" "}
                          {member.district}, {member.state} - {member.pin}
                        </div>
                      </Col>
                      <Col xs={12} md={6}>
                        <div className="p-2 border rounded bg-light">
                          <strong>Member Since:</strong> <br />{" "}
                          {member.member_since}
                        </div>
                      </Col>
                      <Col xs={12} md={6}>
                        <div className="p-2 border rounded bg-light">
                          <strong>Membership Expiry Date:</strong> <br />
                          <span
                            className={
                              isExpired
                                ? "text-danger fw-bold"
                                : isExpiringSoon
                                  ? "text-warning fw-bold"
                                  : ""
                            }
                          >
                            {member.expiry_date}
                          </span>
                        </div>
                      </Col>
                    </Row>

                    {hasSuccessfulRenewalPayment && (
                      <div
                        className="mt-4 p-4 rounded-4 text-center"
                        style={{
                          maxWidth: 500,
                          margin: "0 auto",
                          border: "1px solid #86efac",
                          background:
                            "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)",
                          boxShadow: "0 8px 20px rgba(16, 185, 129, 0.18)",
                        }}
                      >
                        <h5
                          className="fw-bold mb-2"
                          style={{ color: "#065f46" }}
                        >
                          Renewal payment has been done
                        </h5>
                        <p className="mb-0" style={{ color: "#065f46" }}>
                          Your renewal request is submitted and waiting for
                          admin approval.
                        </p>
                      </div>
                    )}
                    {!hasSuccessfulRenewalPayment && isExpiringSoon && (
                      <div
                        className="mt-4 p-4 rounded-4 text-center"
                        style={{
                          maxWidth: 500,
                          margin: "0 auto",
                          border: "1px solid #f3cc6b",
                          background:
                            "linear-gradient(135deg, #fff8e1 0%, #ffefc2 100%)",
                          boxShadow: "0 10px 24px rgba(255, 193, 7, 0.22)",
                        }}
                      >
                        <div className="mb-2">
                          <span
                            className="px-3 py-1 rounded-pill fw-semibold"
                            style={{
                              backgroundColor: "#fff3cd",
                              color: "#8a6d00",
                              border: "1px solid #f1d47a",
                              fontSize: "0.85rem",
                            }}
                          >
                            Renewal Reminder
                          </span>
                        </div>
                        <h5
                          className="fw-bold mb-2"
                          style={{ color: "#7a5a00" }}
                        >
                          Your membership is expiring soon
                        </h5>
                        <p className="mb-3" style={{ color: "#5f4a00" }}>
                          Renew now to keep your membership benefits and avoid
                          interruption.
                        </p>

                        <Button
                          variant="warning"
                          className="w-100 fw-bold py-2"
                          onClick={handleRenewPayment}
                          disabled={renewLoading}
                        >
                          Renew Now - Pay ₹500
                        </Button>
                      </div>
                    )}
                    {!hasSuccessfulRenewalPayment && isExpired && (
                      <div
                        className="mt-4 p-4 rounded-4 text-center"
                        style={{
                          maxWidth: 500,
                          margin: "0 auto",
                          border: "1px solid #ef9a9a",
                          background:
                            "linear-gradient(135deg, #fff0f0 0%, #ffdcdc 100%)",
                          boxShadow: "0 10px 24px rgba(220, 53, 69, 0.2)",
                        }}
                      >
                        <h3 className="text-danger fw-bold position-relative d-inline-block px-3 py-2 border border-danger rounded mb-3">
                          <span>EXPIRED</span>
                        </h3>
                        <h5
                          className="fw-bold mb-2"
                          style={{ color: "#842029" }}
                        >
                          Renew today to reactivate your membership
                        </h5>
                        <p className="mb-3" style={{ color: "#7f1d1d" }}>
                          Your card has expired. Complete renewal now to
                          continue access without delay.
                        </p>

                        <Button
                          variant="danger"
                          className="w-100 fw-bold py-2"
                          onClick={handleRenewPayment}
                          disabled={renewLoading}
                        >
                          Reactivate Membership - Pay ₹500
                        </Button>
                      </div>
                    )}
                    {renewTransactionDetails?.state === "COMPLETED" && (
                      <div className="text-center mt-4">
                        <p className="fw-semibold mb-1 text-success">
                          ✅ Renewal payment completed.
                        </p>
                        <p className="text-muted mb-0">
                          Please wait for admin approval.
                        </p>
                        {renewOrderId && (
                          <p className="text-muted mt-2 mb-0">
                            Transaction ID: {renewOrderId}
                          </p>
                        )}
                      </div>
                    )}
                  </Card.Body>
                </Card>
              )
            )}
          </div>
        </Col>
      </Row>
    </div>
  );
}

export default SearchMember;
